import { createPortal } from 'preact/compat'
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks'
import {
  fullRangePower,
  pendingRangeOverrides,
  powerRange,
  resetAllFilters,
  selectedAnodeChemistries,
  selectedCathodeChemistries,
  selectedFormFactors,
  selectedManufacturers
} from '../../state/filterStore.js'
import {
  basis,
  domain,
  freePlotColorBy,
  freePlotConnectPoints,
  freePlotMarkerShape,
  freePlotXAxis,
  freePlotXAxisScale,
  freePlotYAxis,
  freePlotYAxisScale,
  freePlotZAxis,
  settingsVisible
} from '../../state/globalSignals.js'
import {
  activePreset,
  currentRegression,
  generateInsight,
  getPreset,
  getPresetsByCategory,
  getUserModeKPIs,
  plotMode,
  PRESET_CATEGORIES,
  readPresetFromURL
} from '../../state/plotPresets.js'
import { ColorPaletteSettings } from '../shared/ColorPaletteSettings.jsx'
import {
  IconChevronLeft,
  IconChevronRight,
  IconCpu,
  IconFactory,
  IconFlask,
  IconMinus,
  IconSliders,
  IconTarget,
  IconTrendingDown,
  IconTrendingUp,
  IconZap
} from '../shared/Icons.jsx'
import { AVAILABLE_KPIS } from './FreePlotECharts.jsx'

// Map icon names to components
const CATEGORY_ICONS = {
  IconZap: IconZap,
  IconFlask: IconFlask,
  IconCpu: IconCpu,
  IconFactory: IconFactory,
  IconTrendingUp: IconTrendingUp
}

// Map insight icon names to components
const INSIGHT_ICONS = {
  flask: IconFlask,
  cpu: IconCpu,
  factory: IconFactory,
  bolt: IconZap,
  trend: IconTrendingUp,
  trendingUp: IconTrendingUp,
  trendingDown: IconTrendingDown,
  minus: IconMinus
}

// Build KPI options from AVAILABLE_KPIS for dropdown (Expert mode)
// Pads label segments to fixed column widths per group for table-like alignment
function buildKPIOptions() {
  const options = {}
  Object.entries(AVAILABLE_KPIS).forEach(([category, kpis]) => {
    const displayCategory = category.charAt(0).toUpperCase() + category.slice(1)

    // Parse each label into segments split by " / "
    const parsed = kpis.map(kpi => {
      const parts = kpi.label.split(' / ')
      const unit = kpi.unit ? `(${kpi.unit})` : ''
      return { id: kpi.id, parts, unit }
    })

    // Compute max width per column within this group
    const maxCols = Math.max(...parsed.map(p => p.parts.length))
    const colWidths = []
    for (let i = 0; i < maxCols; i++) {
      colWidths[i] = Math.max(...parsed.map(p => (p.parts[i] || '').length))
    }

    options[displayCategory] = parsed.map(({ id, parts, unit }) => {
      let label
      if (parts.length === 1) {
        label = `${parts[0]}  ${unit}`.trim()
      } else {
        const padded = parts.map((part, i) => part.padEnd(colWidths[i]))
        label = `${padded.join('  │  ')}  ${unit}`.trim()
      }
      return { value: id, label }
    })
  })
  return options
}

const KPI_OPTIONS = buildKPIOptions()

// ===== KPI Definitions for Inline Tooltips =====
// Maps KPI IDs to concise definition text shown on hover
const KPI_DEFINITIONS = {
  // ── Electrical: Energy ──
  energy_c10_abs: 'The C/10 energy is measured by discharging the cell at 25\u00b0C from 100% SOC with a constant current of 0.1C until reaching the lower voltage limit. This is the absolute energy in Wh.',
  energy_c10_grav: 'The gravimetric energy density is the C/10 energy divided by the cell mass (Wh/kg).',
  energy_c10_vol: 'The volumetric energy density is the C/10 energy divided by the cell volume (Wh/L).',

  // ── Electrical: Capacity ──
  capacity_c10_abs: 'The C/10 capacity is the total charge delivered during a C/10 discharge at 25\u00b0C from 100% SOC to the lower voltage limit. Absolute value in Ah.',
  capacity_c10_grav: 'The gravimetric capacity density is the C/10 capacity divided by the cell mass (Ah/kg).',
  capacity_c10_vol: 'The volumetric capacity density is the C/10 capacity divided by the cell volume (Ah/L).',

  // ── Electrical: Power ──
  power_peak_abs: 'The peak power is the mean power at the maximum current the cell can supply for 5 minutes, discharging from 100% SOC at 25\u00b0C.',
  power_peak_grav: 'The gravimetric peak power density is the peak power divided by cell mass (W/kg).',
  power_peak_vol: 'The volumetric peak power density is the peak power divided by cell volume (W/L).',
  power_cont_abs: 'The continuous power is the mean power at the highest current that completely discharges the cell without overheating.',
  power_cont_grav: 'The gravimetric continuous power density is the continuous power divided by cell mass (W/kg).',
  power_cont_vol: 'The volumetric continuous power density is the continuous power divided by cell volume (W/L).',

  // ── Electrical: Current ──
  current_peak_abs: 'The peak current is the maximum current the cell can supply for 5 minutes from 100% SOC at 25\u00b0C.',
  current_peak_grav: 'The gravimetric peak current density is the peak current divided by cell mass (A/kg).',
  current_peak_vol: 'The volumetric peak current density is the peak current divided by cell volume (A/L).',
  current_cont_abs: 'The continuous current is the highest current that completely discharges the cell without overheating.',
  current_cont_grav: 'The gravimetric continuous current density is the continuous current divided by cell mass (A/kg).',
  current_cont_vol: 'The volumetric continuous current density is the continuous current divided by cell volume (A/L).',

  // ── Electrical: Voltage & Impedance ──
  voltage: 'The mean voltage of a C/10 discharge at 25\u00b0C from 100% SOC to the lower voltage limit.',
  R_ac_25: 'The AC resistance is the real part of the impedance spectrum at the intersection with the real axis, measured at SOC 80% and 25\u00b0C. Comparable to the 1 kHz resistance.',
  R_pol_25: 'The polarisation resistance is the size of the impedance semicircle, defined as the difference between the AC resistance and the real part at the local minimum of the negative imaginary part.',
  R_dc_25: 'The DCIR is the voltage difference before and 10 seconds after a discharge current pulse, divided by the pulse current. Measured at SOC 80% and 25\u00b0C.',

  // ── Thermal ──
  P_loss_dc_25_abs: 'The heat power is the power dissipated as heat during the standard condition discharge. Determined from the difference between OCV energy and measured energy.',
  P_loss_dc_25_grav: 'The gravimetric heat power density is the heat power divided by cell mass (W/kg).',
  P_loss_dc_25_vol: 'The volumetric heat power density is the heat power divided by cell volume (W/L).',
  W_loss_dc_25_abs: 'The heat energy is the total energy dissipated as heat during the standard condition discharge (Wh).',
  W_loss_dc_25_grav: 'The gravimetric heat energy density is the heat energy divided by cell mass (Wh/kg).',
  W_loss_dc_25_vol: 'The volumetric heat energy density is the heat energy divided by cell volume (Wh/L).',
  T_max_dc_25: 'The maximum surface temperature reached during the standard condition discharge at 25\u00b0C.',
  eta_dc_25: 'The discharge efficiency is the ratio of usable energy to theoretically available energy (W_dc / W_ocv).',

  // ── Mechanical ──
  mass: 'The total mass of the cell in grams.',
  volume: 'The total volume of the cell in cm\u00b3.',
  surface_area: 'The total surface area of the cell in cm\u00b2.',

  // ── Quality ──
  aging_cap_loss_abs: 'The absolute capacity loss measured during Batemo\u2019s initial cycling validation at 25\u00b0C. Indicates post-formation capacity loss in Ah.',
  aging_cap_loss: 'The relative capacity loss during initial cycling, expressed as a percentage of the initial capacity.',
  aging_eis_increase_abs: 'The absolute impedance growth during initial cycling at 25\u00b0C, in m\u03a9.',
  aging_eis_increase: 'The relative impedance growth during initial cycling, expressed as a percentage.',
  batch_cap_variation_abs: 'The standard deviation of capacity across a batch of cells (absolute, Ah). Indicates manufacturing consistency.',
  batch_cap_variation: 'The relative standard deviation of capacity across a batch (%). Lower means more consistent.',
  batch_eis_variation_abs: 'The standard deviation of impedance across a batch of cells (absolute, m\u03a9).',
  batch_eis_variation: 'The relative standard deviation of impedance across a batch (%). Lower means more consistent.',

  // ── Date ──
  year: 'The year the cell was characterized by Batemo, derived from the measurement date.'
}

/**
 * Inline tooltip component for KPI definitions.
 * Shows a "?" icon next to the axis label; hover reveals the definition.
 * Uses portal rendering to avoid overflow clipping.
 */
function KpiInfoTooltip({ kpiId }) {
  const iconRef = useRef(null)
  const [visible, setVisible] = useState(false)
  const [pos, setPos] = useState({ top: 0, left: 0 })

  const text = KPI_DEFINITIONS[kpiId]
  if (!text) return null

  const handleEnter = useCallback(() => {
    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect()
      // Position tooltip below the icon, aligned to the right edge
      setPos({ top: rect.bottom + 8, left: rect.right })
      setVisible(true)
    }
  }, [])

  const handleLeave = useCallback(() => setVisible(false), [])

  return (
    <span
      ref={iconRef}
      className="axis-info-icon"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      data-tooltip={visible ? text : undefined}
    >
      ?
      {visible && createPortal(
        <div
          className="axis-info-tooltip-portal"
          style={{
            position: 'fixed',
            top: `${pos.top}px`,
            left: `${pos.left}px`,
            transform: 'translateX(-100%)',
            zIndex: 999999
          }}
        >
          {text}
        </div>,
        document.body
      )}
    </span>
  )
}

const COLOR_OPTIONS = [
  { value: 'main-chemistry', label: 'Main Chemistry' },
  { value: 'cathode-chemistry', label: 'Cathode Chemistry' },
  { value: 'anode-chemistry', label: 'Anode Chemistry' },
  { value: 'technology', label: 'Technology' },
  { value: 'format', label: 'Format' },
  { value: 'manufacturer', label: 'Manufacturer' },
  { value: 'year', label: 'Year' },
]

// Build SIZE_OPTIONS from AVAILABLE_KPIS (same as axis options)
const SIZE_OPTIONS_GROUPED = {}
SIZE_OPTIONS_GROUPED['Fixed'] = [{ value: 'none', label: 'Fixed Size' }]
Object.entries(AVAILABLE_KPIS).forEach(([category, kpis]) => {
  // Map category names to display names
  let displayCategory
  if (category === 'dc25') {
    displayCategory = 'Standard'
  } else if (category === 'aging') {
    displayCategory = 'Post-Formation Loss'
  } else if (category === 'batch') {
    displayCategory = 'Cell-to-Cell Variation'
  } else {
    displayCategory = category.charAt(0).toUpperCase() + category.slice(1)
  }

  SIZE_OPTIONS_GROUPED[displayCategory] = kpis.map(kpi => ({
    value: kpi.id,
    label: `${kpi.label} ${kpi.unit ? `(${kpi.unit})` : ''}`.trim()
  }))
})

// ===== Quick Analysis Preset Selector Component =====
function QuickAnalysisSection({ onPresetSelect }) {
  // Open the category of the active preset, or fall back to the first category
  const [expandedCategory, setExpandedCategory] = useState(() => {
    if (activePreset.value) {
      const presetData = getPreset(activePreset.value)
      if (presetData?.category) return presetData.category
    }
    return PRESET_CATEGORIES[0]?.id || null
  })
  const currentPreset = activePreset.value
  const currentPresetData = currentPreset ? getPreset(currentPreset) : null

  // On mount: read preset from URL (or fall back to localStorage) and apply it
  useEffect(() => {
    if (plotMode.value !== 'user') return
    const urlPreset = readPresetFromURL()
    const presetToApply = urlPreset || activePreset.value
    if (presetToApply) {
      handlePresetClick(presetToApply)
      const presetData = getPreset(presetToApply)
      if (presetData?.category) {
        setExpandedCategory(presetData.category)
      }
    }
  }, [])

  const handlePresetClick = (presetId) => {
    const preset = getPreset(presetId)
    if (!preset) return

    // Apply preset settings
    freePlotXAxis.value = preset.xAxis
    freePlotYAxis.value = preset.yAxis
    freePlotColorBy.value = preset.colorBy
    freePlotZAxis.value = 'none' // Guided presets are all 2D

    // Apply basis/domain if required
    if (preset.requiredBasis) {
      basis.value = preset.requiredBasis
    }
    if (preset.requiredDomain) {
      domain.value = preset.requiredDomain
    }

    // Reset all filters before applying preset-specific ones
    resetAllFilters()

    // Apply filters
    if (preset.filters) {

      if (preset.filters.shape) {
        // Map shape to format groups (includes 'cylindrical' itself for cells without specific format)
        const formatMap = {
          'cylindrical': ['18650', '21700', '26650', '26700', '4680', '4695', '46120', 'cylindrical']
        }
        const formats = preset.filters.shape.flatMap(s => formatMap[s] || [])
        if (formats.length > 0) {
          selectedFormFactors.value = formats
        }
      }
      if (preset.filters.format) {
        selectedFormFactors.value = preset.filters.format
      }
      if (preset.filters.anodeChemistry) {
        selectedAnodeChemistries.value = preset.filters.anodeChemistry
      }
      if (preset.filters.cathodeChemistry) {
        selectedCathodeChemistries.value = preset.filters.cathodeChemistry
      }
      if (preset.filters.minPowerGrav) {
        // Store as pending override so it survives async metadata reload
        pendingRangeOverrides.value = {
          ...(pendingRangeOverrides.value || {}),
          powerMin: preset.filters.minPowerGrav
        }
        // Also apply immediately for instant feedback
        powerRange.value = {
          min: preset.filters.minPowerGrav,
          max: fullRangePower.value.max
        }
      }
    }

    // Set active preset
    activePreset.value = presetId
    onPresetSelect?.(presetId)
  }

  const handleClearPreset = () => {
    activePreset.value = null
    pendingRangeOverrides.value = null
    // Clear preset-applied filters
    selectedFormFactors.value = []
    selectedAnodeChemistries.value = []
    selectedCathodeChemistries.value = []
    selectedManufacturers.value = []
  }

  // Helper to render insight content for a preset
  const renderInsights = (presetId) => {
    // Insight based on trend analysis (evolution presets)
    const trendInsight = generateInsight(presetId, currentRegression.value)
    if (trendInsight) {
      const insightClass = trendInsight.isFallback
        ? 'neutral'
        : trendInsight.isPositiveNews ? 'positive' : 'negative'
      return (
        <div className={`preset-insight ${insightClass}`}>
          <div className="preset-insight__disclaimer">Batemo's Interpretation</div>
          <div className="preset-insight__content">
            <div className="preset-insight__text">{trendInsight.insight}</div>
            {trendInsight.confidence && (
              <div className="preset-insight__confidence">
                {(() => {
                  const TrendIcon = INSIGHT_ICONS[trendInsight.iconName] || IconTrendingUp
                  return <TrendIcon size={14} className="preset-insight__icon" />
                })()}
                {trendInsight.confidence} (R² = {trendInsight.r2})
              </div>
            )}
          </div>
        </div>
      )
    }

    // Static interpretation from preset definition (non-evolution presets)
    const preset = getPreset(presetId)
    if (preset?.interpretation) {
      return (
        <div className="preset-insight cluster">
          <div className="preset-insight__disclaimer">Batemo's Interpretation</div>
          <div className="preset-insight__content">
            <div className="preset-insight__text">{preset.interpretation}</div>
          </div>
        </div>
      )
    }

    return null
  }

  return (
    <div className="settings-section settings-section--card quick-analysis-section">
      <div className="settings-section-header">
        <IconTarget size={16} style={{ color: '#ea5b0c' }} />
        <span>Insights</span>
      </div>

      {/* Preset Categories */}
      <div className="preset-categories" data-tour="graph-presets">
        {PRESET_CATEGORIES.map(category => {
          const isExpanded = expandedCategory === category.id
          const presets = getPresetsByCategory(category.id)
          const IconComponent = CATEGORY_ICONS[category.icon]

          return (
            <div
              key={category.id}
              className={`preset-category ${isExpanded ? 'expanded' : ''}`}
            >
              <button
                className="preset-category__header"
                onClick={() => setExpandedCategory(isExpanded ? null : category.id)}
              >
                <span className="preset-category__icon">
                  {IconComponent && <IconComponent size={16} />}
                </span>
                <span className="preset-category__label">{category.label}</span>
                <span className="preset-category__arrow">▼</span>
              </button>

              {isExpanded && (
                <div className="preset-list">
                  {presets.map(preset => {
                    const isActive = currentPreset === preset.id

                    return (
                      <div key={preset.id} className="preset-item-wrapper">
                        <button
                          className={`preset-item ${isActive ? 'active' : ''}`}
                          onClick={() => handlePresetClick(preset.id)}
                        >
                          <span className="preset-item__indicator"></span>
                          <div className="preset-item__content">
                            <div className="preset-item__question">{preset.question}</div>
                          </div>
                        </button>

                        {/* Show explanation and insights directly below selected preset */}
                        {isActive && (
                          <div className="preset-info-inline">
                            <button className="preset-info-inline__close" onClick={handleClearPreset} title="Clear preset">
                              ✕
                            </button>
                            <div className="preset-info-inline__explanation">{preset.explanation}</div>
                            {renderInsights(preset.id)}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Reset Button (when preset is active) */}
      {currentPreset && (
        <button className="preset-reset-btn" onClick={handleClearPreset}>
          <span>↺</span>
          <span>Clear Preset & Filters</span>
        </button>
      )}
    </div>
  )
}

// ===== Mode Toggle Component =====
function PlotModeToggle() {
  return (
    <div className="settings-section settings-section--card">
      <div className="settings-section-header">
        <IconSliders size={16} style={{ color: '#ea5b0c' }} />
        <span>Mode</span>
      </div>
      <div className="plot-mode-toggle" data-tour="graph-mode-toggle">
        <div className="plot-mode-toggle__switch">
          <button
            className={`plot-mode-toggle__option ${plotMode.value === 'user' ? 'active' : ''}`}
            onClick={() => {
              plotMode.value = 'user'
              freePlotZAxis.value = 'none'
            }}
          >
            Guided
          </button>
          <button
            className={`plot-mode-toggle__option ${plotMode.value === 'expert' ? 'active' : ''}`}
            onClick={() => plotMode.value = 'expert'}
          >
            Expert
          </button>
        </div>
      </div>
    </div>
  )
}

export function FreePlotSettingsOverlay() {
  const [showAdvanced, setShowAdvanced] = useState(false)

  // Get appropriate KPI options based on mode
  const kpiOptions = useMemo(() => {
    if (plotMode.value === 'expert') {
      return KPI_OPTIONS
    }
    // User mode: simplified options based on basis/domain
    const userKPIs = getUserModeKPIs(domain.value, basis.value)
    return {
      'Main Metrics': userKPIs.map(kpi => ({
        value: kpi.id,
        label: `${kpi.label} ${kpi.unit ? `(${kpi.unit})` : ''}`.trim()
      }))
    }
  }, [plotMode.value, basis.value, domain.value])

  return (
    <div
      className={`settings-drawer ${!settingsVisible.value ? 'settings-drawer--collapsed' : ''}`}
    >
      {/* Collapsed State - Vertical Text + Icon */}
      {!settingsVisible.value && (
        <div
          className="settings-drawer__collapsed-content"
          onClick={() => settingsVisible.value = true}
        >
          <button
            className="settings-drawer__collapsed-icon"
            title="Expand Settings"
          >
            <IconChevronLeft size={20} />
          </button>
          <div className="settings-drawer__collapsed-text">
            PLOT SETTINGS
          </div>
        </div>
      )}

      {/* Expanded State - Normal Content */}
      {settingsVisible.value && (
        <>
          {/* Header with integrated collapse button */}
          <div className="settings-header">
            <div className="settings-title">Plot Settings</div>
            <button
              className="settings-collapse-btn"
              onClick={(e) => {
                e.stopPropagation()
                settingsVisible.value = false
              }}
              title="Collapse Settings"
            >
              <IconChevronRight size={16} />
            </button>
          </div>

          {/* Main Settings */}
          <div className="settings-body">
            {/* Mode Toggle */}
            <PlotModeToggle />

            {/* Quick Analysis Section - Guided Mode Only */}
            {plotMode.value === 'user' && (
            <QuickAnalysisSection onPresetSelect={() => {}} />
            )}

            {/* Axes Section - Expert Mode Only */}
            {plotMode.value === 'expert' && (
            <div className="settings-section settings-section--card" data-tour="graph-expert-axes">
              <div className="settings-section-header">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#ea5b0c' }}>
                  <line x1="3" y1="12" x2="21" y2="12"></line>
                  <polyline points="8 7 3 12 8 17"></polyline>
                  <polyline points="16 7 21 12 16 17"></polyline>
                </svg>
                <span>Axes</span>
              </div>

              <div className="settings-group">
                <label className="settings-label settings-label--axis">
                  X-Axis
                  <KpiInfoTooltip kpiId={freePlotXAxis.value} />
                </label>
                <select
                  className="settings-select settings-select--kpi"
                  value={freePlotXAxis.value}
                  onChange={(e) => { freePlotXAxis.value = e.target.value; activePreset.value = null }}
                >
                  {Object.entries(kpiOptions).map(([group, options]) => (
                    <optgroup label={group}>
                      {options.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              <div className="settings-group">
                <label className="settings-label settings-label--axis">
                  Y-Axis
                  <KpiInfoTooltip kpiId={freePlotYAxis.value} />
                </label>
                <select
                  className="settings-select settings-select--kpi"
                  value={freePlotYAxis.value}
                  onChange={(e) => { freePlotYAxis.value = e.target.value; activePreset.value = null }}
                >
                  {Object.entries(kpiOptions).map(([group, options]) => (
                    <optgroup label={group}>
                      {options.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              <div className="settings-group">
                <label className="settings-label settings-label--axis">
                  Z-Axis (Optional)
                  {freePlotZAxis.value !== 'none' && <KpiInfoTooltip kpiId={freePlotZAxis.value} />}
                </label>
                <select
                  className="settings-select settings-select--kpi"
                  value={freePlotZAxis.value}
                  onChange={(e) => { freePlotZAxis.value = e.target.value; activePreset.value = null }}
                >
                  <option value="none">None (2D)</option>
                  {Object.entries(kpiOptions).map(([group, options]) => (
                    <optgroup label={group}>
                      {options.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
            </div>
            )}

            {/* Advanced Options Toggle - Expert Mode Only */}
            {plotMode.value === 'expert' && (
            <button
              className="settings-advanced-toggle"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              <IconSliders size={14} />
              <span>Advanced Options</span>
              <span className="toggle-arrow" style={{ transform: showAdvanced ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                ▼
              </span>
            </button>
            )}

            {/* Advanced Options */}
            {plotMode.value === 'expert' && showAdvanced && (
              <div className="settings-advanced">
                {/* Axis Configuration */}
                <div className="advanced-section">
                  <div className="advanced-section-title">Axis Configuration</div>
                  <div className="advanced-row">
                    <label>X-Axis Scale</label>
                    <select
                      value={freePlotXAxisScale.value}
                      onChange={(e) => freePlotXAxisScale.value = e.target.value}
                    >
                      <option value="linear">Linear</option>
                      <option value="log">Logarithmic</option>
                    </select>
                  </div>
                  <div className="advanced-row">
                    <label>Y-Axis Scale</label>
                    <select
                      value={freePlotYAxisScale.value}
                      onChange={(e) => freePlotYAxisScale.value = e.target.value}
                    >
                      <option value="linear">Linear</option>
                      <option value="log">Logarithmic</option>
                    </select>
                  </div>
                </div>

                {/* Marker Configuration */}
                <div className="advanced-section">
                  <div className="advanced-section-title">Marker Configuration</div>
                  <div className="advanced-row">
                    <label>Shape</label>
                    <select
                      value={freePlotMarkerShape.value}
                      onChange={(e) => freePlotMarkerShape.value = e.target.value}
                    >
                      <option value="circle">Circle</option>
                      <option value="rect">Rectangle</option>
                      <option value="roundRect">Rounded Rectangle</option>
                      <option value="triangle">Triangle</option>
                      <option value="diamond">Diamond</option>
                      <option value="pin">Pin</option>
                      <option value="arrow">Arrow</option>
                    </select>
                  </div>
                </div>

                {/* Analysis Tools */}
                <div className="advanced-section">
                  <div className="advanced-section-title">Analysis Tools</div>
                  <label className="settings-checkbox">
                    <input
                      type="checkbox"
                      checked={freePlotConnectPoints.value}
                      onChange={(e) => freePlotConnectPoints.value = e.target.checked}
                    />
                    <span>Connect Points</span>
                  </label>
                </div>

                {/* Color Palette */}
                <ColorPaletteSettings />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
